import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../../../component/common/Loader';
import SearchInput from '../../../../component/common/Input/SearchInput';
import Table from '../../../../component/common/Table';
import Pagination from '../../../../component/common/table2/Pagination';
import { fetchCompanyVisionBoardUsers } from './companyVisionBoardApi';
import { TableCard } from '../../../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Searchable users table for the Vision Board section.
 *
 * Boards and items are separate columns on purpose: somebody with four boards
 * and no items has tried the feature four times and got nowhere, which looks
 * identical to enthusiasm if you only count boards.
 *
 * Every user of the population is listed, including those who have never made a
 * board — they show dashes rather than zeros, so "hasn't used the feature"
 * reads differently from "made a board and left it empty".
 */
const VisionBoardUsersTable = ({
  fetcher = fetchCompanyVisionBoardUsers,
  onOpenUser,
  tableName,
  showCompanyColumn = false,
  department = ''
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({ page, perPage: PER_PAGE, search, department });
        const mapped = (response.data || []).map((row) => {
          const hasBoards = row.totalBoards > 0;
          return {
            ...row,
            boardsText: hasBoards ? row.totalBoards.toLocaleString() : '-',
            itemsText: hasBoards ? row.totalItems.toLocaleString() : '-',
            activeText: hasBoards ? row.activeBoards.toLocaleString() : '-',
            reminderText: hasBoards ? row.withReminder.toLocaleString() : '-',
            lastBoardDate: row.lastBoardDate || '-'
          };
        });
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error('VisionBoardUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoader(false);
      }
    },
    [fetcher, department]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Boards', key: 'boardsText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Items', key: 'itemsText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Active', key: 'activeText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Reminders', key: 'reminderText', sortable: false, align: 'center', type: 'text3' },
    {
      title: 'Last Board',
      key: 'lastBoardDate',
      sortable: false,
      align: 'center',
      type: 'text3'
    },
    { title: '', key: 'action', align: 'right', type: 'action', arrowClickFun: onOpenUser }
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

VisionBoardUsersTable.propTypes = {
  fetcher: PropTypes.func,
  onOpenUser: PropTypes.func,
  tableName: PropTypes.string,
  showCompanyColumn: PropTypes.bool,
  department: PropTypes.string
};

export default VisionBoardUsersTable;
